import { useContext, useMemo, useCallback } from 'react'

import { AuthStateContext } from '@percent/admin-dashboard/context/auth/authContext/AuthContext'
import {
  DomainPermission,
  DomainPermissions,
  PermissionString,
  PermissionCheck,
  PermissionRequirement,
  PermissionsInput
} from './usePermissions.types'

const hasPermission = (userPermissions: DomainPermission[], requiredPermission: DomainPermission): boolean => {
  return userPermissions.some(
    perm => perm.domainId === requiredPermission.domainId && perm.permission === requiredPermission.permission
  )
}

const isPermissionRequirement = (permissions: PermissionCheck): permissions is PermissionRequirement =>
  typeof permissions === 'object' && !Array.isArray(permissions) && !('domainId' in permissions)

const parsePermissionString = (permissionString: PermissionString): DomainPermission => {
  const [domainId, permission] = permissionString.split(':')

  if (!domainId || !permission) {
    throw new Error(`Invalid permission format: "${permissionString}". Expected format: "domain:permission"`)
  }

  return { domainId, permission }
}

const normalizePermission = (permission: PermissionString | DomainPermission): DomainPermission => {
  if (typeof permission === 'string') {
    return parsePermissionString(permission)
  }

  return permission
}

const groupPermissionsByDomain = (permissions: DomainPermission[]): DomainPermissions => {
  return permissions.reduce((acc, { domainId, permission }) => {
    if (!acc[domainId]) {
      acc[domainId] = []
    }

    acc[domainId].push(permission)

    return acc
  }, {} as DomainPermissions)
}

const evaluatePermissionsInput = (
  input: PermissionsInput,
  userPermissions: DomainPermission[],
  requireAll = true
): boolean => {
  if (Array.isArray(input)) {
    if (requireAll) {
      return input.every(permission => {
        const normalizedPermission = normalizePermission(permission)

        return hasPermission(userPermissions, normalizedPermission)
      })
    }

    return input.some(permission => {
      const normalizedPermission = normalizePermission(permission)

      return hasPermission(userPermissions, normalizedPermission)
    })
  }

  const normalizedPermission = normalizePermission(input)

  return hasPermission(userPermissions, normalizedPermission)
}

const evaluatePermissionRequirement = (
  requirement: PermissionRequirement,
  userPermissions: DomainPermission[]
): boolean => {
  const { all, any } = requirement

  if (!all && !any) {
    return true
  }

  const allPass = !all || evaluatePermissionsInput(all, userPermissions, true)
  const anyPass = !any || evaluatePermissionsInput(any, userPermissions, false)

  return allPass && anyPass
}

export const usePermissions = () => {
  const context = useContext(AuthStateContext)

  if (context === undefined) {
    throw new Error('usePermissions must be used within an AuthContext')
  }

  const domainPermissions = useMemo(() => {
    if (!context.permissionsSet || !context.permissions.length) {
      return {}
    }

    return groupPermissionsByDomain(context.permissions)
  }, [context.permissions, context.permissionsSet])

  const canViewRolesOnAnyDomain = useMemo(
    () => Object.keys(domainPermissions).some(domainId => domainPermissions[domainId]?.includes('can_view_roles')),
    [domainPermissions]
  )

  const userHasPermissions = useCallback(
    (permissions: PermissionCheck) => {
      if (!context.permissionsSet || !context.permissions.length) {
        return false
      }

      if (isPermissionRequirement(permissions)) {
        if ('all' in permissions || 'any' in permissions) {
          return evaluatePermissionRequirement(permissions, context.permissions)
        }

        return Object.keys(permissions).length === 0
      }

      return evaluatePermissionsInput(permissions, context.permissions)
    },
    [context.permissionsSet, context.permissions]
  )

  return {
    userHasPermissions,
    permissions: context.permissions,
    domainPermissions,
    canViewRolesOnAnyDomain,
    permissionsSet: context.permissionsSet
  }
}
